import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as CoverImagesIcon } from '@/assets/icons/menu/cover-images.svg';
import { ReactComponent as DistributionIcon } from '@/assets/icons/menu/distribution.svg';
import { ReactComponent as EditorialIcon } from '@/assets/icons/menu/editorial.svg';
import { ReactComponent as EytvFilesIcon } from '@/assets/icons/menu/eytv-files.svg';
import { ReactComponent as FinancesIcon } from '@/assets/icons/menu/finances.svg';
// import { ReactComponent as GamificationIcon } from '@/assets/icons/menu/gamification.svg';
import { ReactComponent as ImageToolIcon } from '@/assets/icons/menu/image-tool.svg';
import { ReactComponent as InvoicesIcon } from '@/assets/icons/menu/invoices.svg';
// import { ReactComponent as VideoAnalysisIcon } from "@/assets/icons/menu/video-analysis.svg";
import { ReactComponent as LinksLibraryIcon } from '@/assets/icons/menu/links-library.svg';
import { ReactComponent as MergeTipsIcon } from '@/assets/icons/menu/merge-tips.svg';
// import { ReactComponent as DistributionIcon } from "@/assets/icons/menu/distribution.svg";
// import { ReactComponent as OptimisationIcon } from "@/assets/icons/menu/optimisation.svg";
import { ReactComponent as PerformanceIcon } from '@/assets/icons/menu/performance.svg';
// import { ReactComponent as PostProductionIcon } from "@/assets/icons/menu/post-production.svg";
import { ReactComponent as ProductionIcon } from '@/assets/icons/menu/production.svg';
import { ReactComponent as ResearchIcon } from '@/assets/icons/menu/research.svg';
import { ReactComponent as SettingsIcon } from '@/assets/icons/menu/settings.svg';
// import { ReactComponent as StrategyChangelogIcon } from '@/assets/icons/menu/strategy-changelog.svg';
import { ReactComponent as TestingIcon } from '@/assets/icons/menu/testing.svg';
import { ReactComponent as ThumbnailsIcon } from '@/assets/icons/menu/thumbnails.svg';
import { ReactComponent as IgTrackerIcon } from '@/assets/icons/menu/tracker-icon.svg';
import { ReactComponent as FreelancersIcon } from '@/assets/icons/menu/users.svg';
import { ReactComponent as VibeboneIcon } from '@/assets/icons/menu/vibebone.svg';
import Text from '@/components/Text';
import useHasPermission from '@/hooks/useHasPermission';
import useIsGeneralMode from '@/hooks/useIsGeneralMode';
import useIsGodMode from '@/hooks/useIsGodMode';
import { Permission, Role } from '@/types/resources';
import { numFormatter } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';
import { hexToRgba } from '@/utils/theme';

import NavigationItemsCount from './NavigationItemsCount';
import NavigationSubMenu from './NavigationSubMenu';
import VibecodeMenu from './VibecodeMenu';

const Block = styled.nav`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => hexToRgba(theme.colors.white, 0.08)};
  &:last-of-type {
    border: 0;
  }
`;

const Heading = styled(Text)`
  opacity: 0.5;
  margin: 0 0 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const Link = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  ${Text} {
    opacity: 0.6;
    line-height: 18px;
    transition: 0.2s ease-out opacity;
  }
  &:hover,
  &.active {
    ${Text} {
      opacity: 1;
    }
  }
  svg {
    margin-right: 8px;
  }
`;

const Li = styled.li<{ $hasCount: boolean }>`
  margin: 0 0 15px;
  ${({ $hasCount }) =>
    $hasCount &&
    css`
      display: flex;
      justify-content: space-between;
      align-items: center;
    `}
`;

export interface NavItem {
  title: string;
  to?: string;
  Icon?: React.FunctionComponent;
  navItems?: NavItem[];
  exceptOnGodMode?: boolean;
  permissions?: Permission[];
  count?: any;
  hidden?: boolean;
  general?: boolean;
  deprecated?: boolean;
  end?: boolean;
  excludeRoles?: Role[];
}

interface NavBlocks {
  title: string;
  hidden?: boolean;
  navItems: NavItem[];
  exceptOnGeneralMode?: boolean;
  permissions?: Permission[];
  deprecated?: boolean;
}
const Navigation = () => {
  const user = useSelector((state: RootState) => state.auth.user!);

  const { teamId } = useParams();

  const dispatch = useDispatch<Dispatch>();

  const isFreelancer = user.freelancer?.id;

  // Team Members don't review invoices, unless they've been given company /
  // department invoice access.
  const canReviewScopedInvoices = useHasPermission(
    'view companies invoices',
    'view departments invoices',
  );

  useEffect(() => {
    if (teamId) {
      dispatch.navigationCounter.getAssignTopicsCounter(teamId!);
    }
  }, [dispatch.navigationCounter, teamId]);

  const isGodMode = useIsGodMode();

  const isGeneralMode = useIsGeneralMode();

  const navigationCounter = useSelector(
    (rootState: RootState) => rootState.navigationCounter,
  );

  const blocks: NavBlocks[] = [
    {
      title: 'Overview',
      exceptOnGeneralMode: true,
      navItems: [
        // TODO: uncommend when needed
        // {
        //   title: "Published Videos",
        //   to: "video-analysis",
        //   Icon: VideoAnalysisIcon,
        // },
        // {
        //   title: "Performance",
        //   to: "performance",
        //   Icon: PerformanceIcon,
        //   hidden: true,
        // },
        {
          title: 'Finances',
          to: 'finances',
          Icon: FinancesIcon,
          hidden: true,
        },
        // {
        //   title: 'Strategy Changelog',
        //   to: 'strategy-changelog',
        //   Icon: StrategyChangelogIcon,
        // },
        {
          title: 'Settings',
          to: 'settings',
          Icon: SettingsIcon,
          permissions: ['manage settings'],
          exceptOnGodMode: true,
        },
      ],
    },
    {
      title: 'Content',
      exceptOnGeneralMode: true,
      navItems: [
        {
          permissions: ['view reports', 'view employee engagement'],
          title: 'Performance',
          Icon: PerformanceIcon,
          hidden: true,
          navItems: [
            {
              title: 'Facebook',
              to: 'reports/facebook',
              permissions: ['view reports'],
            },
            {
              title: 'Youtube',
              to: 'reports/youtube',
              permissions: ['view reports'],
            },
            // {
            //   title: 'Cross Platform',
            //   to: 'reports/cross-platform',
            // },
            // {
            //   title: 'Market Share',
            //   to: 'reports/market-share',
            // },
            // {
            //   title: 'Testing',
            //   to: 'reports/testing-report',
            // },
            {
              title: 'Third party',
              to: 'reports/third-party-report',
              permissions: ['view reports'],
            },
            {
              title: 'Third party test',
              to: 'reports/third-party-test-report',
              permissions: ['view reports'],
            },
            // {
            //   title: 'Recommendations',
            //   to: 'reports/recommendations-report',
            // },
            {
              title: 'Top 100 FB Reels',
              to: 'reports/top-100-fb-reels',
              permissions: ['view reports'],
            },
            {
              title: 'Newsflare Bonus',
              to: 'reports/newsflare-bonus',
              permissions: ['view reports'],
            },
            {
              title: 'Amazon',
              to: 'reports/amazon',
              permissions: ['view reports'],
            },
            {
              title: 'Employee Engagement',
              to: 'reports/employee-engagement',
              permissions: ['view employee engagement'],
            },
            {
              title: 'Facebook Competitors',
              to: 'reports/facebook-competitors',
              permissions: ['view reports'],
            },
            {
              title: 'Wow',
              to: 'reports/wow',
              permissions: ['view reports'],
            },
            {
              title: 'Caters Media',
              to: 'reports/caters-media',
              permissions: ['view reports'],
            },
            {
              title: 'Other Reports',
              to: 'reports/other',
              permissions: ['view reports'],
            },
          ],
        },
        {
          title: 'Research',
          Icon: ResearchIcon,
          permissions: ['view research'],
          hidden: true,
          navItems: [
            {
              title: 'Assign Topics',
              to: 'assign-topics',
              count: navigationCounter['assign-topics']
                ? numFormatter(navigationCounter['assign-topics'])
                : null,
            },
            {
              title: 'Top Script topics',
              to: 'top-script-topics',
            },
            {
              title: 'Top Thumb topics',
              to: 'top-thumb-topics',
            },
            {
              title: 'Top Title topics',
              to: 'top-title-topics',
            },
            {
              title: 'Top Video types',
              to: 'top-video-types',
            },
            {
              title: 'Top Videos',
              to: 'top-videos',
            },
            {
              title: 'Report',
              to: 'research-report',
            },
          ],
        },
        {
          title: 'Testing',
          Icon: TestingIcon,
          exceptOnGodMode: true,
          permissions: ['use youtube testing'],
          navItems: [
            {
              title: 'Optimisation',
              to: 'optimisation-projects',
            },
            {
              title: 'Settings',
              to: 'testing-settings',
              hidden: true,
            },
          ],
        },
        {
          title: 'Distribution',
          Icon: DistributionIcon,
          hidden: true,
          exceptOnGodMode: true,
          deprecated: true,
          navItems: [
            {
              title: 'Schedule',
              to: 'distribution/schedule',
              end: true,
            },
            {
              title: 'Prepared',
              to: 'distribution/prepared',
              end: true,
            },
            {
              title: 'Draft',
              to: 'distribution/draft',
              end: true,
            },
            {
              title: 'Predictions',
              to: 'distribution/predictions',
              end: true,
            },
            {
              title: 'Conditional Reposts',
              to: 'distribution/conditional-reposts',
              end: true,
            },
            {
              title: 'Project Summary',
              to: 'distribution/projects',
              end: false,
            },
            {
              title: 'Video Summary',
              to: 'distribution/videos',
              end: false,
            },
            // TODO: uncomment distribution heatmap page link
            // {
            //   title: "Heatmap",
            //   to: "distribution/heatmap",
            //   end: true
            // },
            {
              title: 'Tips Library',
              to: 'distribution/tips',
              end: true,
            },
            {
              title: 'Subtitles Generator',
              to: 'distribution/subtitles-generator',
              end: false,
            },
            {
              title: 'ML Projects',
              to: 'distribution/ml-projects',
              end: false,
            },
            {
              title: 'ML Translate & Voiceover',
              to: 'distribution/ml-languages',
              end: false,
            },
            {
              title: 'ML Distribution Backlog',
              to: 'distribution/ml-backlog',
              end: false,
            },
            {
              title: 'Settings',
              to: 'distribution/settings/employees',
              end: false,
            },
          ],
        },
        {
          title: 'Production',
          Icon: ProductionIcon,
          hidden: true,
          exceptOnGodMode: true,
          deprecated: true,
          navItems: [
            {
              title: 'Project List',
              to: 'pre-production',
            },
            // {
            //   title: 'Project Events',
            //   to: 'production/events/sent',
            // },
            {
              title: 'Settings',
              to: 'production-settings',
            },
          ],
        },
        {
          title: 'Visual Assets',
          Icon: ThumbnailsIcon,
          deprecated: true,
          exceptOnGodMode: true,
          navItems: [
            // {
            //   title: 'Generator',
            //   to: 'thumbnails-generator',
            // },
            // {
            //   title: 'PNG Extractor',
            //   to: 'png-extractor',
            // },
            {
              title: 'Top Thumbnails',
              to: 'top-thumbnails',
            },
            {
              general: true,
              title: 'Image Tool',
              to: 'image-tool/type/recents',
            },
          ],
        },
      ],
    },
    // {
    //   title: "Content",
    //   navItems: [
    //     {
    //       title: "Research",
    //       to: "research",
    //       Icon: ResearchIcon,
    //     },
    //     {
    //       title: "Production",
    //       to: "production",
    //       Icon: ProductionIcon,
    //     },
    //     {
    //       title: "Post-Production",
    //       to: "post-production",
    //       Icon: PostProductionIcon,
    //     },
    //     {
    //       title: "Optimisation",
    //       to: "optimisation",
    //       Icon: OptimisationIcon,
    //     },
    //     {
    //       title: "Distribution",
    //       to: "distribution",
    //       Icon: DistributionIcon,
    //     },
    //   ],
    // },
    {
      title: 'Organisation',
      navItems: [
        {
          title: 'Organisation',
          Icon: SettingsIcon,
          permissions: ['manage organisation settings'],
          navItems: [
            {
              general: true,
              title: 'Departments',
              to: 'departments',
            },
            {
              general: true,
              title: 'Account Numbers',
              to: 'account-numbers',
            },
            {
              general: true,
              title: 'Companies',
              to: 'companies',
            },
            {
              general: true,
              title: 'Subunits',
              to: 'subunits',
            },
            {
              general: true,
              title: 'Roles',
              to: 'roles',
            },
          ],
        },
        {
          title: 'Editorial',
          Icon: EditorialIcon,
          permissions: ['use distribution queue'],
          navItems: [
            {
              general: true,
              title: 'Distribution Queue',
              to: 'editorial-distribution-queue',
            },
            {
              general: true,
              title: 'Scheduled Actions',
              to: 'dq-scheduled-actions',
            },
            {
              general: true,
              title: 'Comments Checker',
              to: 'dq-comments-checker',
            },
            {
              general: true,
              title: 'FB Video Stories',
              to: 'facebook-video-stories',
            },
            {
              general: true,
              title: 'FB Image Stories',
              to: 'facebook-image-stories',
            },
            {
              general: true,
              title: 'DQ Feed',
              to: 'dq-feed',
            },
            // {
            //   general: true,
            //   title: 'Posts Testing (deprecated)',
            //   to: 'bp-testing-projects',
            // },
            // {
            //   general: true,
            //   title: 'Post Distribution (deprecated)',
            //   to: 'post-distribution',
            // },
            // {
            //   general: true,
            //   title: 'Image Distribution (deprecated)',
            //   to: 'image-distribution',
            // },
          ],
        },
        {
          title: 'Invoices  & Assets',
          Icon: InvoicesIcon,
          ...(!isFreelancer && {
            permissions: [
              'add invoices',
              'view your invoices',
              'view companies invoices',
              'view departments invoices',
              'view all invoices',
              'manage invoices',
              'manage payment members',
            ],
          }),
          navItems: [
            {
              general: true,
              title: 'Add invoice',
              to: 'invoices/create',
              end: true,
              permissions: ['add invoices', 'manage invoices'],
            },
            {
              general: true,
              title: 'Invoice List',
              to: 'invoices',
              end: true,
              permissions: [
                'view your invoices',
                'view companies invoices',
                'view departments invoices',
                'view all invoices',
                'manage invoices',
              ],
            },
            {
              general: true,
              title: 'Deleted List',
              to: 'invoices/deleted',
              end: true,
              permissions: ['view all invoices', 'manage invoices'],
            },
            {
              general: true,
              title: 'Receiver List',
              to: 'invoices/receiver-list',
              end: true,
              permissions: ['manage payment members'],
            },
            ...(isFreelancer
              ? [
                  {
                    general: true,
                    title: 'Generate Invoice',
                    to: 'freelancers/invoices',
                    end: true,
                  },
                ]
              : []),
            {
              general: true,
              title: 'Invoices Approvals',
              to: 'freelancers/invoices/approvals',
              end: true,
              ...(!canReviewScopedInvoices && {
                excludeRoles: ['Team Member'] as Role[],
              }),
            },
            {
              general: true,
              title: 'Changelog',
              to: 'invoices/invoices-changelog',
              end: true,
              permissions: ['manage invoices'],
            },
          ],
        },
        {
          title: 'Freelancers',
          Icon: FreelancersIcon,
          ...(!isFreelancer && {
            permissions: [
              'view your freelancers',
              'view companies freelancers',
              'view departments freelancers',
              'view companies vacations',
              'view departments vacations',
              'manage freelancers',
              'view all freelancers',
              'manage freelancer work email',
            ] as Permission[],
          }),
          navItems: [
            {
              general: true,
              title: 'All Freelancers',
              to: 'freelancers/type/employed',
              end: true,
              permissions: [
                'view your freelancers',
                'view companies freelancers',
                'view departments freelancers',
                'view all freelancers',
                'manage freelancer work email',
              ],
            },
            {
              general: true,
              title: 'Vacation Approvals',
              to: 'freelancers/vacation-approvals',
              end: true,
              permissions: [
                'view your freelancers',
                'view companies vacations',
                'view departments vacations',
                'view all freelancers',
                'manage freelancers',
              ],
            },
            ...(isFreelancer
              ? [
                  {
                    general: true,
                    title: 'Request Vacation',
                    to: 'freelancers/request-vacation',
                    end: true,
                  },
                ]
              : []),
            {
              general: true,
              title: 'Public Holidays',
              to: 'freelancers/public-holidays',
              end: true,
              permissions: ['manage freelancers'],
            },
          ],
        },
        // {
        //   title: 'Gamification',
        //   Icon: GamificationIcon,
        //   navItems: [
        //     {
        //       general: true,
        //       title: 'Leaderboard Overview',
        //       to: 'gamification/leaderboard/thumb',
        //       end: true,
        //     },
        //     {
        //       general: true,
        //       title: 'Competitions',
        //       to: 'gamification/projects/active',
        //       end: true,
        //     },
        //     {
        //       general: true,
        //       title: 'Bonuses',
        //       to: 'gamification/bonuses/thumb',
        //       visibleOnlyForSuperAdmin: true,
        //       end: true,
        //     },
        //   ],
        // },
        {
          title: 'Importers',
          Icon: ResearchIcon,
          permissions: ['use importers'],
          navItems: [
            {
              general: true,
              title: 'Google Products SERP',
              to: 'google-products-serp',
            },
            {
              general: true,
              title: 'Amazon Products SERP',
              to: 'amazon-products-serp',
            },
            {
              general: true,
              title: 'Instagram Images SERP',
              to: 'instagram-images-serp',
            },
            {
              general: true,
              title: 'Social Posts Importer',
              to: 'social-posts-importer-tool',
            },
          ],
        },
        {
          title: 'Instagram Tracker',
          Icon: IgTrackerIcon,
          permissions: ['use IG tracker'],
          navItems: [
            {
              general: true,
              title: 'Profiles',
              to: 'ig-profiles',
            },
            {
              general: true,
              title: 'Posts',
              to: 'ig-posts',
            },
          ],
        },

        {
          title: 'Image Tool',
          Icon: ImageToolIcon,
          navItems: [
            {
              general: true,
              title: 'Image Generator',
              to: 'image-tool/type/recents',
            },
            {
              general: true,
              title: 'Images Library',
              to: 'image-tool/library',
            },
          ],
        },
        {
          general: true,
          title: 'Cover Images',
          Icon: CoverImagesIcon,
          to: 'cover-images/generate/one',
          end: false,
        },
        {
          general: true,
          permissions: ['use video compiler'],
          title: 'Video Compiler',
          Icon: MergeTipsIcon,
          to: 'video-compilations',
          end: false,
        },
        {
          title: 'Video Factory',
          permissions: ['use video factory'],
          Icon: ThumbnailsIcon,
          navItems: [
            {
              general: true,
              title: 'Videos',
              to: 'video-factory',
            },
            {
              general: true,
              title: 'Memes',
              to: 'animated-memes',
            },
            {
              general: true,
              title: 'Articles',
              to: 'bp-article-reels',
            },
          ],
        },
        {
          general: true,
          permissions: ['view all channels list'],
          title: 'All Channels',
          to: 'all-channels',
          Icon: PerformanceIcon,
        },
        {
          general: true,
          title: 'Links Library',
          to: 'links-library',
          Icon: LinksLibraryIcon,
        },
        {
          general: true,
          title: 'eYTV Files',
          permissions: ['use eytv testing'],
          to: 'eytv-files',
          Icon: EytvFilesIcon,
        },
        {
          general: true,
          title: 'VibeBone',
          Icon: VibeboneIcon,
        },
        {
          title: 'Settings',
          Icon: SettingsIcon,
          permissions: ['manage Meta business accounts', 'manage users'],
          navItems: [
            {
              general: true,
              title: 'Business Accounts',
              permissions: ['manage Meta business accounts'],
              to: 'meta-business-accounts',
            },
            {
              general: true,
              title: 'Users Management',
              permissions: ['manage users'],
              to: 'users-management',
            },
          ],
        },
      ],
    },
  ];

  const navBlocksFilter = (navBlock: NavBlocks) => {
    if (navBlock.hidden && user.email === 'bps.backbone.testing@gmail.com') {
      return false;
    }

    if (navBlock.deprecated) {
      return false;
    }

    if (
      !!navBlock.permissions &&
      !user.permissions!.find((p) => navBlock.permissions!.includes(p))
    ) {
      return false;
    }

    if (isGeneralMode && navBlock.exceptOnGeneralMode) {
      return false;
    }

    return true;
  };

  const navItemsFilter = (navItem: NavItem) => {
    if (navItem.hidden && user.email === 'bps.backbone.testing@gmail.com') {
      return false;
    }

    if (navItem.deprecated) {
      return false;
    }

    if (
      !!navItem.permissions &&
      !user.permissions!.find((p) => navItem.permissions!.includes(p))
    ) {
      return false;
    }

    if (navItem.excludeRoles?.includes(user.role.name)) {
      return false;
    }

    if (isGodMode && navItem.exceptOnGodMode) {
      return false;
    }

    return true;
  };

  return (
    <>
      {blocks.filter(navBlocksFilter).map((block, index) => (
        <Block key={index}>
          <Heading as="h3" $type="smallBold" $color="white">
            {block.title}
          </Heading>
          <ul>
            {block.navItems.filter(navItemsFilter).map((navItem, index) => (
              <Li $hasCount={!!navItem.count} key={index}>
                {navItem.title === 'VibeBone' ? (
                  <VibecodeMenu navItem={navItem} />
                ) : navItem.navItems ? (
                  <NavigationSubMenu
                    navItem={{
                      ...navItem,
                      navItems: navItem.navItems.filter(navItemsFilter),
                    }}
                  />
                ) : (
                  <>
                    <Link
                      end={navItem.end}
                      to={
                        navItem.general
                          ? `/general/${navItem.to}`
                          : `/teams/${teamId}/${navItem.to}`
                      }
                      className={(isActive) => isActive && 'active'}
                    >
                      {!!navItem.Icon && <navItem.Icon />}
                      <Text as="span" $color="white" $type="smallBold">
                        {navItem.title}
                      </Text>
                    </Link>
                    {!!navItem.count && (
                      <NavigationItemsCount count={navItem.count} />
                    )}
                  </>
                )}
              </Li>
            ))}
          </ul>
        </Block>
      ))}
    </>
  );
};

export default Navigation;
